import { useEffect, useMemo, useState } from 'react';
import {
	ButtonDarkHighlighted,
	ButtonGreen,
	ButtonGrey,
	ButtonPurple,
	ButtonWhite,
} from '../shared/Buttons';
import { Input } from '../shared/Input';
import { FormRow, InputRow } from '../shared/InputRow';
import { Modal, ModalProps } from '../shared/Modal';
import {
	checkCurrencyFormatFull,
	checkCurrencyFormatPartly,
	checkIntFormatFull,
	convertEnumsToOptions,
	convertObjectToOptions,
	getOptionList,
} from '../../scripts/scripts';
import { Select } from '../shared/Select';
import {
	TixBought,
	TixCompletedStatusTypes,
	TixCurrencyTypes,
	TixSale,
	TixSecondaryName,
	TixSecondaryTypes,
	TixTransferNames,
	TixTransferTypes,
} from '../../types/TixTypes';
import toast from 'react-hot-toast';
import { useApiMutation } from '../../scripts/api/api_v2';
import BigNumber from 'bignumber.js';
import { AddSalePayload, GETEventResponseBase } from '../../types/TixAPITypes';
import { FlexCol, FlexRowMiddle } from '../shared/Flex';
import { Text } from '../shared/Text';
import { secondaryWithOptionalReference } from './ModalSaledata';
import LogoWeList from '../../../public/logos/welist.svg?react';
import LogoViagogo from '../../../public/logos/viagogo.svg?react';
import LogoTixstock from '../../../public/logos/tixstock.svg?react';
import LogoLysted from '../../../public/logos/lysted.svg?react';
import { CategoryHeader } from '../shared/CategoryHeader';

export function ModalAddSale({
	closeModal,
	seatIds,
	event,
	onSaleAdded,
}: ModalProps & {
	seatIds: TixBought['id'][];
	event: GETEventResponseBase;
	onSaleAdded: (sale: TixSale) => void;
}) {
	const isFine = seatIds.length < 1;

	const [saving, setSaving] = useState<boolean>(false);
	const [formState, setFormState] = useState({
		secondary: '',
		amount: '',
		reference: '',
		quantity: '',
		transferType: isFine ? TixTransferTypes.FINE : '',
		buyerEmail: '',
		buyerName: '',
		amountNative: '',
		amountCurrency: '',
		note: '',
	});

	const { fetchData: postSaleAPI } = useApiMutation('tix.post-sale');

	const onChange = (name: keyof typeof formState, value: string | number) => {
		setFormState((state) => ({ ...state, [name]: value }));
	};

	const onClickSave = async () => {
		try {
			setSaving(true);

			const saleBody: AddSalePayload = isFine
				? {
						tixId: [],
						eventId: event.id,
						secondary: formState.secondary as TixSecondaryTypes,
						quantity: 0,
						amount: formState.amount ? new BigNumber(formState.amount).toNumber() : null,
						reference: formState.reference,
						transferType: TixTransferTypes.FINE,
						status: TixCompletedStatusTypes.ACTION_NEEDED,
						buyerEmail: null,
						buyerName: null,
						amountReceivedDate: null,
						amountNative: formState.amountNative ? new BigNumber(formState.amountNative).toNumber() : null,
						amountCurrency: (formState.amountCurrency as TixCurrencyTypes) || null,
						note: formState.note,
					}
				: {
						tixId: seatIds,
						eventId: event.id,
						secondary: formState.secondary as TixSecondaryTypes,
						quantity:
							formState.secondary === TixSecondaryTypes.UNSOLD
								? 0
								: new BigNumber(formState.quantity).toNumber(),
						amount:
							formState.secondary === TixSecondaryTypes.UNSOLD
								? 0
								: formState.amount
									? new BigNumber(formState.amount).toNumber()
									: null,
						reference: formState.secondary === TixSecondaryTypes.UNSOLD ? null : formState.reference,
						transferType: formState.transferType as TixTransferTypes,
						status: TixCompletedStatusTypes.ACTION_NEEDED,
						buyerEmail: formState.buyerEmail || null,
						buyerName: formState.buyerName || null,
						amountReceivedDate: null,
						amountNative: formState.amountNative ? new BigNumber(formState.amountNative).toNumber() : null,
						amountCurrency: (formState.amountCurrency as TixCurrencyTypes) || null,
						note: formState.note,
					};

			const savedSaleRes = await postSaleAPI({
				body: saleBody,
			});
			if (!savedSaleRes?.success) {
				toast.error(savedSaleRes?.error ?? 'Failed to add sale!');
				return;
			}
			onSaleAdded(savedSaleRes.data);
			toast.success(`Sale ${formState.reference} added!`);
			closeModal();
		} catch (err) {
			console.error(err);
		} finally {
			setSaving(false);
		}
	};

	const checkInputs = () => {
		if (!formState['secondary']) return false;
		if (!isFine && !formState['transferType']) return false;
		if (formState.secondary !== TixSecondaryTypes.UNSOLD && !isFine && !formState['quantity']) return false;
		if (!optionalReference && formState.secondary !== TixSecondaryTypes.UNSOLD && !formState['reference']) {
			return false;
		}
		if (!formState['amountNative'] && formState['amountCurrency']) return false;
		if (formState['amountNative'] && !formState['amountCurrency']) return false;
		return true;
	};

	const seatInfo = useMemo(() => {
		const idSet = new Set(seatIds);
		const seats: TixBought[] = Object.values(event.seats).filter((b) => idSet.has(b.id));
		return seats;
	}, [seatIds, event]);

	const isUnsold = formState.secondary === TixSecondaryTypes.UNSOLD;
	const optionalReference = secondaryWithOptionalReference.includes(formState.secondary as TixSecondaryTypes);

	const onClickViagogo = () => {
		setFormState((state) => ({ ...state, secondary: TixSecondaryTypes.VIAGOGO }));
	};

	const onClickWelist = () => {
		setFormState((state) => ({ ...state, secondary: TixSecondaryTypes.WELIST }));
	};

	const onClickLysted = () => {
		setFormState((state) => ({ ...state, secondary: TixSecondaryTypes.LYSTED }));
	};

	const onClickTixstock = () => {
		setFormState((state) => ({ ...state, secondary: TixSecondaryTypes.TIXSTOCK }));
	};

	const transferButtons = (() => {
		if (formState.secondary === TixSecondaryTypes.VIAGOGO) {
			return (
				<>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.TRANSFER)}
						$focus={formState.transferType === TixTransferTypes.TRANSFER}
					>
						Transfer
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.PRE_UPLOAD)}
						$focus={formState.transferType === TixTransferTypes.PRE_UPLOAD}
					>
						Pre-upload
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.E_TICKET)}
						$focus={formState.transferType === TixTransferTypes.E_TICKET}
					>
						E-Ticket
					</ButtonDarkHighlighted>
				</>
			);
		} else if (
			[TixSecondaryTypes.TIXSTOCK, TixSecondaryTypes.WELIST].includes(formState.secondary as TixSecondaryTypes)
		) {
			return (
				<>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.TRANSFER)}
						$focus={formState.transferType === TixTransferTypes.TRANSFER}
					>
						Transfer
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.SMP)}
						$focus={formState.transferType === TixTransferTypes.SMP}
					>
						SMP
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.E_TICKET)}
						$focus={formState.transferType === TixTransferTypes.E_TICKET}
					>
						E-Ticket
					</ButtonDarkHighlighted>
				</>
			);
		} else if (formState.secondary === TixSecondaryTypes.LYSTED) {
			return (
				<>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.BARCODE)}
						$focus={formState.transferType === TixTransferTypes.BARCODE}
					>
						Barcode
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.SMP)}
						$focus={formState.transferType === TixTransferTypes.SMP}
					>
						SMP
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted
						onClick={() => onChange('transferType', TixTransferTypes.TRANSFER)}
						$focus={formState.transferType === TixTransferTypes.TRANSFER}
					>
						Transfer
					</ButtonDarkHighlighted>
				</>
			);
		} else {
			return (
				<>
					<ButtonDarkHighlighted onClick={() => onChange('transferType', TixTransferTypes.TRANSFER)}>
						Transfer
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted onClick={() => onChange('transferType', TixTransferTypes.SMP)}>
						SMP
					</ButtonDarkHighlighted>
					<ButtonDarkHighlighted onClick={() => onChange('transferType', TixTransferTypes.E_TICKET)}>
						E-Ticket
					</ButtonDarkHighlighted>
				</>
			);
		}
	})();

	useEffect(() => {
		if (formState.secondary === TixSecondaryTypes.TM_RESALE && formState.transferType !== TixTransferTypes.RESALE) {
			setFormState((state) => ({ ...state, transferType: TixTransferTypes.RESALE }));
		}
	}, [formState]);

	return (
		<Modal title={!isFine ? 'Create a Sale' : 'Add Fine'} closeModal={closeModal}>
			<FormRow>
				<CategoryHeader>Sale Details</CategoryHeader>
			</FormRow>
			{!isFine && (
				<>
					<FormRow>
						<FlexCol style={{ rowGap: '0.5rem' }}>
							{seatInfo.map((seat, seatIndex) => {
								const details = [seat.section];
								if (seat.row) details.push(`Row ${seat.row}`);
								if (seat.seat) details.push(`Seat ${seat.seat}`);
								return (
									<FlexRowMiddle key={`add_sale_seat_${seatIndex}`} style={{ columnGap: '0.5rem' }}>
										<Text $color="text_desc_main" $size="1.3rem" $weight="400">
											{details.join(', ')}
										</Text>
										<Text $color="text_desc_alt" $size="1.1rem" $weight="400">
											- {event.orders[seat.orderId].reference}
										</Text>
									</FlexRowMiddle>
								);
							})}
						</FlexCol>
					</FormRow>
				</>
			)}

			<FormRow>
				<ButtonGreen onClick={onClickViagogo} $focus={formState.secondary === TixSecondaryTypes.VIAGOGO}>
					<LogoViagogo />
				</ButtonGreen>
				<ButtonGreen onClick={onClickTixstock} $focus={formState.secondary === TixSecondaryTypes.TIXSTOCK}>
					<LogoTixstock />
				</ButtonGreen>
				<ButtonPurple onClick={onClickWelist} $focus={formState.secondary === TixSecondaryTypes.WELIST}>
					<LogoWeList />
				</ButtonPurple>
				<ButtonPurple onClick={onClickLysted} $focus={formState.secondary === TixSecondaryTypes.LYSTED}>
					<LogoLysted />
				</ButtonPurple>
			</FormRow>

			{!isFine && <FormRow>{transferButtons}</FormRow>}

			<FormRow>
				<InputRow label="Secondary">
					<Select
						value={formState['secondary']}
						onChange={(v) => onChange('secondary', v)}
						options={getOptionList('Secondary', convertObjectToOptions(TixSecondaryName))}
					/>
				</InputRow>
				<InputRow label="Transfer Type">
					<Select
						value={formState.transferType}
						onChange={(v) => onChange('transferType', v)}
						options={getOptionList(
							'Transer Type',
							convertEnumsToOptions(TixTransferTypes, TixTransferNames),
						)}
						disabled={formState.secondary === TixSecondaryTypes.TM_RESALE || isFine}
					/>
				</InputRow>
			</FormRow>

			<FormRow style={{ marginTop: '3rem' }}>
				<CategoryHeader>Order Details</CategoryHeader>
			</FormRow>

			{!isUnsold && (
				<FormRow>
					<InputRow label={`Reference ${optionalReference ? '(Optional)' : ''}`}>
						<Input
							value={formState['reference']}
							onChange={(v) => onChange('reference', v)}
							placeholder="# 123456/UK1"
						/>
					</InputRow>
				</FormRow>
			)}

			{!isUnsold && (
				<FormRow>
					<InputRow label="Amount (Optional)">
						<Input
							value={formState['amount']}
							onChange={(v) => onChange('amount', v)}
							placeholder="12.34"
							onChangeMap={checkCurrencyFormatPartly}
							onBlurMap={checkCurrencyFormatFull}
						/>
					</InputRow>
					{!isFine && (
						<InputRow label="Quantity">
							<Input
								value={formState['quantity']}
								onChange={(v) => onChange('quantity', v)}
								placeholder="1"
								onChangeMap={checkIntFormatFull}
							/>
						</InputRow>
					)}
				</FormRow>
			)}

			{!isUnsold && (
				<FormRow>
					<InputRow label="Amount Currency (Optional)">
						<Select
							value={formState['amountCurrency']}
							onChange={(v) => onChange('amountCurrency', v)}
							options={getOptionList(
								'Currency',
								convertEnumsToOptions(TixCurrencyTypes, TixCurrencyTypes),
							)}
						/>
					</InputRow>
					<InputRow label="Amount Native (Optional)">
						<Input
							value={formState['amountNative']}
							onChange={(v) => onChange('amountNative', v)}
							placeholder="12.34"
							onChangeMap={checkCurrencyFormatPartly}
							onBlurMap={checkCurrencyFormatFull}
						/>
					</InputRow>
				</FormRow>
			)}

			<FormRow style={{ marginTop: '3rem' }}>
				<CategoryHeader>Transfer Details</CategoryHeader>
			</FormRow>

			{!isFine && !isUnsold && (
				<FormRow>
					<InputRow label="Buyer Email (Optional)">
						<Input
							value={formState['buyerEmail']}
							onChange={(v) => onChange('buyerEmail', v)}
							placeholder="abc@gmail.com"
						/>
					</InputRow>
					<InputRow label="Buyer Name (Optional)">
						<Input
							value={formState['buyerName']}
							onChange={(v) => onChange('buyerName', v)}
							placeholder="John Doe"
						/>
					</InputRow>
				</FormRow>
			)}

			<FormRow>
				<InputRow label="Note (Optional)">
					<Input
						value={formState['note']}
						onChange={(v) => onChange('note', v)}
						placeholder="Custom notes..."
					/>
				</InputRow>
			</FormRow>

			<FormRow>
				<ButtonGrey onClick={closeModal}>Cancel</ButtonGrey>
				<ButtonWhite disabled={!checkInputs() || saving} onClick={onClickSave}>
					Save
				</ButtonWhite>
			</FormRow>
		</Modal>
	);
}
